import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './RoleManagement.module.css';
import { roleApi, Role, RoleCreate, RoleUpdate, RoleUser } from '../../services/roleApi';

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showUsersModal, setShowUsersModal] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [roleUsers, setRoleUsers] = useState<RoleUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [formData, setFormData] = useState<{ name: string; description: string }>({
    name: '',
    description: ''
  });
  const [saving, setSaving] = useState<boolean>(false);

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await roleApi.getAllRoles();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles. Please try again later.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setFormData({ name: role.name, description: role.description });
    setShowModal(true);
  };

  const handleViewUsers = async (role: Role) => {
    if (role.user_count === 0) {
      toast.info(`No users are assigned to the role "${role.name}"`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoadingUsers(true);
      setViewingRole(role);
      setShowUsersModal(true);
      
      const users = await roleApi.getRoleUsers(role.Roles_ID);
      setRoleUsers(users);
    } catch (error) {
      console.error('Error fetching role users:', error);
      toast.error('Failed to load users for this role. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      setShowUsersModal(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setViewingRole(null);
    setRoleUsers([]);
  };

  const handleDeleteRole = (role: Role) => {
    // Use toast.warn with custom confirmation UI
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete the role "{role.name}"?</p>
        {role.user_count > 0 && (
          <p className="mb-3 text-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            This role is assigned to {role.user_count} user(s). Please remove role assignments first.
          </p>
        )}
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            disabled={role.user_count > 0}
            onClick={async () => {
              toast.dismiss();
              if (role.user_count > 0) return;
              
              try {
                await roleApi.deleteRole(role.Roles_ID);
                setRoles(prevRoles => prevRoles.filter(r => r.Roles_ID !== role.Roles_ID));
                toast.success(`Role "${role.name}" deleted successfully`, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              } catch (error) {
                console.error('Error deleting role:', error);
                toast.error('Failed to delete role. Please try again.', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Role name is required', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      setSaving(true);
      
      if (editingRole) {
        // Update existing role
        const updatedRole = await roleApi.updateRole(editingRole.Roles_ID, {
          name: formData.name.trim(),
          description: formData.description.trim()
        });
        
        setRoles(prevRoles => 
          prevRoles.map(role => 
            role.Roles_ID === editingRole.Roles_ID ? updatedRole : role
          )
        );
        
        toast.success(`Role "${updatedRole.name}" updated successfully`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        // Create new role
        const newRole = await roleApi.createRole({
          name: formData.name.trim(),
          description: formData.description.trim()
        });
        
        setRoles(prevRoles => [...prevRoles, newRole]);
        
        toast.success(`Role "${newRole.name}" created successfully`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      setShowModal(false);
      setFormData({ name: '', description: '' });
      setEditingRole(null);
      
    } catch (error) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to save role: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ name: '', description: '' });
    setEditingRole(null);
  };

  // Filter roles based on search term
  const filteredRoles = roles.filter(role => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      role.name.toLowerCase().includes(search) ||
      (role.description && role.description.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading roles...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin">
                <i className="bi bi-house-door me-1"></i>Admin
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Role Management
            </li>
          </ol>
        </nav>

        {/* Header with Create Button */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Role Management</h2>
            <p className="text-muted mb-0">
              <i className="bi bi-shield-lock me-2"></i>
              Configure and manage user roles
            </p>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={fetchRoles}
              title="Refresh roles"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleCreateRole}
            >
              <i className="bi bi-plus-circle me-2"></i>
              New Role
            </button>
          </div>
        </div>

        {/* Roles Table Container */}
        <div className={styles.tableContainer}>
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
            <div className="d-flex align-items-center">
              <i className="bi bi-table me-2 text-primary"></i>
              <h5 className="mb-0">User Roles</h5>
            </div>
            <span className="badge bg-primary rounded-pill">
              {filteredRoles.length} {filteredRoles.length === 1 ? 'Role' : 'Roles'}
            </span>
          </div>
          
          {/* Search Bar */}
          <div className="p-3 border-bottom bg-white">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search"></i>
              </span>
              <input 
                type="text"
                className="form-control"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className={styles.desktopTable}>
            <table className={`table ${styles.roleTable}`}>
              <thead>
                <tr>
                  <th>
                    <i className="bi bi-tag me-1"></i>
                    Name
                  </th>
                  <th>
                    <i className="bi bi-card-text me-1"></i>
                    Description
                  </th>
                  <th>
                    <i className="bi bi-people me-1"></i>
                    Users
                  </th>
                  <th>
                    <i className="bi bi-tools me-1"></i>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-5">
                      <div className={styles.emptyState}>
                        {searchTerm ? (
                          <>
                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No roles found</h5>
                            <p className="text-muted mb-3">
                              Try adjusting your search criteria.
                            </p>
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => setSearchTerm('')}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Clear Search
                            </button>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No roles configured</h5>
                            <p className="text-muted mb-3">
                              Create your first role to get started.
                            </p>
                            <button 
                              className="btn btn-primary"
                              onClick={handleCreateRole}
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              Create First Role
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRoles.map((role, index) => (
                    <tr key={role.Roles_ID} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                      <td className={styles.roleTableCell}>
                        <div className={styles.roleName}>
                          <i className="bi bi-shield-fill me-2 text-primary"></i>
                          <span className="fw-medium">{role.name}</span>
                        </div>
                      </td>
                      <td className={styles.roleTableCell}>
                        <div className={styles.roleDescription}>
                          {role.description || <em className="text-muted">No description</em>}
                        </div>
                      </td>
                      <td className={styles.roleTableCell}>
                        <span className={`badge ${role.user_count > 0 ? 'bg-info' : 'bg-secondary'} ${styles.userBadge}`}>
                          <i className="bi bi-people-fill me-1"></i>
                          {role.user_count}
                        </span>
                      </td>
                      <td className={styles.roleTableCell}>
                        <div className={`btn-group ${styles.actionButtons}`}>
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => handleEditRole(role)}
                            title="Edit role"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handleViewUsers(role)}
                            title="View users"
                          >
                            <i className="bi bi-eye-fill"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteRole(role)}
                            disabled={role.user_count > 0}
                            title={role.user_count > 0 ? "Cannot delete role with assigned users" : "Delete role"}
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className={styles.mobileCards}>
            {filteredRoles.length === 0 ? (
              <div className="text-center p-5">
                <div className={styles.emptyState}>
                  {searchTerm ? (
                    <>
                      <i className="bi bi-search display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No roles found</h5>
                      <p className="text-muted mb-3">
                        Try adjusting your search criteria.
                      </p>
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Clear Search
                      </button>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No roles configured</h5>
                      <p className="text-muted mb-3">
                        Create your first role to get started.
                      </p>
                      <button 
                        className="btn btn-primary"
                        onClick={handleCreateRole}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Create First Role
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              filteredRoles.map((role) => (
                <div key={role.Roles_ID} className={styles.roleCard}>
                  <div className={styles.roleCardHeader}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="mb-0 fw-bold">
                        <i className="bi bi-shield-fill me-2 text-primary"></i>
                        {role.name}
                      </h6>
                      <span className={`badge ${role.user_count > 0 ? 'bg-info' : 'bg-secondary'}`}>
                        <i className="bi bi-people-fill me-1"></i>
                        {role.user_count}
                      </span>
                    </div>
                    {role.description && (
                      <p className="text-muted small mb-0">{role.description}</p>
                    )}
                  </div>
                  <div className={styles.roleCardActions}>
                    <button 
                      className="btn btn-sm btn-outline-warning flex-fill"
                      onClick={() => handleEditRole(role)}
                      title="Edit role"
                    >
                      <i className="bi bi-pencil-fill me-1"></i>
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-info flex-fill"
                      onClick={() => handleViewUsers(role)}
                      title="View users"
                    >
                      <i className="bi bi-eye-fill me-1"></i>
                      Users
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger flex-fill"
                      onClick={() => handleDeleteRole(role)}
                      disabled={role.user_count > 0}
                      title={role.user_count > 0 ? "Cannot delete role with assigned users" : "Delete role"}
                    >
                      <i className="bi bi-trash-fill me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal for Create / Edit */}
        {showModal && (
          <div className={`modal fade show d-block ${styles.modalWrapper}`} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className={`bi ${editingRole ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                    {editingRole ? 'Edit Role' : 'Create New Role'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSaveRole}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label htmlFor="roleName" className="form-label">
                        <i className="bi bi-tag me-1"></i>
                        Role Name *
                      </label>
                      <input
                        type="text"
                        id="roleName"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter role name"
                        required
                        maxLength={255}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="roleDescription" className="form-label">
                        <i className="bi bi-card-text me-1"></i>
                        Description
                      </label>
                      <textarea
                        id="roleDescription"
                        className="form-control"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter role description (optional)"
                        rows={4}
                        maxLength={500}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                      disabled={saving}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={saving || !formData.name.trim()}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className={`bi ${editingRole ? 'bi-check-circle' : 'bi-plus-circle'} me-1`}></i>
                          {editingRole ? 'Update Role' : 'Create Role'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal for Viewing Users in a Role */}
        {showUsersModal && viewingRole && (
          <div className={`modal fade show d-block ${styles.modalWrapper}`} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-people-fill me-2"></i>
                    <span className={styles.modalTitleText}>Users: {viewingRole.name}</span>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseUsersModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingUsers ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">Loading users...</div>
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className={styles.desktopUserTable}>
                        <div className="table-responsive">
                          <table className="table table-striped">
                            <thead>
                              <tr>
                                <th><i className="bi bi-person me-1"></i>Name</th>
                                <th><i className="bi bi-envelope me-1"></i>Email</th>
                                <th><i className="bi bi-briefcase me-1"></i>Job Title</th>
                                <th><i className="bi bi-building me-1"></i>Firm</th>
                              </tr>
                            </thead>
                            <tbody>
                              {roleUsers.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="text-center text-muted py-4">
                                    <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                                    <div>No users found for this role.</div>
                                  </td>
                                </tr>
                              ) : (
                                roleUsers.map((user) => (
                                  <tr key={user.User_ID}>
                                    <td>
                                      <strong>
                                        {user.title ? `${user.title} ` : ''}{user.firstname} {user.lastname}
                                      </strong>
                                    </td>
                                    <td>
                                      <a href={`mailto:${user.mail}`} className="text-decoration-none">
                                        {user.mail}
                                      </a>
                                    </td>
                                    <td>
                                      {user.jobtitle || <em className="text-muted">No job title</em>}
                                    </td>
                                    <td>
                                      {user.firm ? (
                                        <span className="badge bg-light text-dark">
                                          {user.firm.name || user.firm.abbrev}
                                        </span>
                                      ) : (
                                        <em className="text-muted">No firm</em>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Mobile Card View */}
                      <div className={styles.mobileUserCards}>
                        {roleUsers.length === 0 ? (
                          <div className="text-center text-muted py-4">
                            <i className="bi bi-inbox display-4 text-muted mb-3 d-block"></i>
                            <div>No users found for this role.</div>
                          </div>
                        ) : (
                          roleUsers.map((user) => (
                            <div key={user.User_ID} className={styles.userCard}>
                              <div className="d-flex align-items-start mb-2">
                                <i className="bi bi-person-circle text-primary me-2" style={{ fontSize: '1.5rem' }}></i>
                                <div className="flex-grow-1">
                                  <strong className="d-block">
                                    {user.title ? `${user.title} ` : ''}{user.firstname} {user.lastname}
                                  </strong>
                                  <a href={`mailto:${user.mail}`} className="text-decoration-none small">
                                    <i className="bi bi-envelope me-1"></i>
                                    {user.mail}
                                  </a>
                                </div>
                              </div>
                              {user.jobtitle && (
                                <div className="mb-1 small">
                                  <i className="bi bi-briefcase me-1 text-muted"></i>
                                  <span className="text-muted">{user.jobtitle}</span>
                                </div>
                              )}
                              {user.firm && (
                                <div className="small">
                                  <i className="bi bi-building me-1 text-muted"></i>
                                  <span className="badge bg-light text-dark">
                                    {user.firm.name || user.firm.abbrev}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseUsersModal}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;
